import {useCallback, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {
  CreateGuestByParticipantPayload,
  CreateGuestByParticipantResponse,
} from '@chekin/api';
import {postMultipleItems} from 'pages/Auth/utils';
import {useCreateGuestByParticipant} from 'hooks/useCreateGuestByParticipant';
import {FailedMultipleSaveResponse, safeCaptureException} from '@chekin/shared';
import {FastAPIServiceError} from '@chekin/core';
import {showToast} from '@chekinapp/ui';

type FailedMultipleGuests = FailedMultipleSaveResponse<
  Omit<CreateGuestByParticipantPayload, 'reservation_id'>,
  FastAPIServiceError
>[];

export type MutateMultipleGuestsSettledResult = {
  success: CreateGuestByParticipantResponse[];
  failed: FailedMultipleGuests;
  threw?: boolean;
};

type Props = {
  onSuccess?: (data: CreateGuestByParticipantResponse[]) => void;
  onError?: (results: FailedMultipleGuests) => void;
  onSettled?: (result: MutateMultipleGuestsSettledResult) => void;
};
function useMutateMultipleGuests({onSuccess, onError, onSettled}: Props) {
  const {t} = useTranslation();
  const {mutateAsync} = useCreateGuestByParticipant();
  const [isMutating, setIsMutating] = useState(false);

  const mutateAsyncMultiple = useCallback(
    async (payload: Omit<CreateGuestByParticipantPayload, 'reservation_id'>[]) => {
      setIsMutating(true);
      try {
        const {failed, success} = await postMultipleItems<
          Omit<CreateGuestByParticipantPayload, 'reservation_id'>,
          FastAPIServiceError,
          CreateGuestByParticipantResponse
        >(payload, mutateAsync);

        if (failed.length > 0) {
          onError?.(failed);
        }

        if (failed.length === 0) {
          onSuccess?.(success);
        }

        onSettled?.({success, failed});
      } catch (error) {
        showToast.error(t('something_went_wrong'));
        safeCaptureException(error, 'multiple guests mutation');
        onSettled?.({success: [], failed: [], threw: true});
      } finally {
        setIsMutating(false);
      }
    },
    [mutateAsync, onSuccess, onError, onSettled, t],
  );

  return {mutateAsyncMultiple, isMutating};
}

export {useMutateMultipleGuests};
